import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSocket } from './socket';
import { callEvents } from './socket';
import { ensureLocalMedia, createPeerSession } from '../utils/peerHelpers';
import {
  playRingtone,
  stopRingtone,
  playHangupSound,
} from '../utils/jgRingtoneEngine';
import axiosConn from '../axiosConn';
import { AuthContext } from './AuthContext';

const CallContext = createContext(null);

const __JG_DEBUG = process.env.NODE_ENV !== 'production';

// --- Config / constants ---
const RING_SEGMENT_SECONDS = 6;
const INCOMING_RING_URL = '/sounds/incoming_call.mp3';
const OUTGOING_RING_URL = '/sounds/outgoing_call.mp3';
const HANGUP_SOUND_URL = '/sounds/hangup_call.mp3';

// Missed / declined call messages (no name, Messenger shows context)
const MISSED_CALL_MESSAGE = 'Missed call — call back when available.';
const DECLINED_CALL_MESSAGE = 'Declined call.';

// --- AudioContext for ring gating (kept for future expansion) ---
const AudioContextClass =
  typeof window !== 'undefined'
    ? window.AudioContext || window.webkitAudioContext || window.mozAudioContext
    : null;

// --- RTC stats helper (local-only, your existing signal-strength logic) ---
function computeRtcStats(statsReport, prevStatsRef) {
  // 🛡 Guard: statsReport missing
  if (!statsReport) return null;

  // 🛡 Guard: prevStatsRef missing or not initialized
  if (!prevStatsRef || typeof prevStatsRef?.current !== 'object') {
    prevStatsRef = { current: {} };
  }

  let bytesRecvAudio = 0;
  let packetsRecvAudio = 0;
  let bytesRecvVideo = 0;
  let packetsRecvVideo = 0;
  let bitrateKbps = 0;
  let packetsPerSecond = 0;

  const inboundAudio = [];
  const inboundVideo = [];
  const candidatesById = {};
  let nominatedPair = null;

  // Convert RTCReport → array
  const statsArray = [];
  statsReport.forEach((v) => statsArray.push(v));

  const prev = prevStatsRef.current || {};
  const now = Date.now();

  statsArray.forEach((stat) => {
    // ======================================================
    // (1) Inbound RTP — AUDIO
    // ======================================================
    if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
      inboundAudio.push(stat);
      bytesRecvAudio += stat.bytesReceived || 0;
      packetsRecvAudio += stat.packetsReceived || 0;

      // Compute AUDIO bitrate + PPS
      if (prev.audio && prev.audio.id === stat.id) {
        const deltaBytes =
          (stat.bytesReceived || 0) - (prev.audio.bytesReceived || 0);

        const deltaStamp =
          (stat.timestamp || now) - (prev.audio.timestamp || now);

        const deltaMs = Math.max(1, deltaStamp);

        if (deltaMs > 0) {
          bitrateKbps = (deltaBytes * 8) / deltaMs;
          packetsPerSecond =
            (((stat.packetsReceived || 0) - (prev.audio.packetsReceived || 0)) /
              deltaMs) *
            1000;
        }
      }

      // Update prevStatsRef.current.audio
      prevStatsRef.current.audio = {
        id: stat.id,
        bytesReceived: stat.bytesReceived || 0,
        timestamp: stat.timestamp || now,
        packetsReceived: stat.packetsReceived || 0,
      };
    }

    // ======================================================
    // (1b) Inbound RTP — VIDEO
    // ======================================================
    if (stat.type === 'inbound-rtp' && stat.kind === 'video') {
      inboundVideo.push(stat);
      bytesRecvVideo += stat.bytesReceived || 0;
      packetsRecvVideo += stat.packetsReceived || 0;
    }

    // ======================================================
    // (2) ICE candidates
    // ======================================================
    if (stat.type === 'local-candidate' || stat.type === 'remote-candidate') {
      candidatesById[stat.id] = stat;
    }

    if (stat.type === 'candidate-pair') {
      const isSelected =
        stat.selected ||
        stat.nominated ||
        stat.state === 'succeeded' ||
        stat.state === 'in-use';

      if (isSelected) {
        if (!nominatedPair || (!nominatedPair.nominated && stat.nominated)) {
          nominatedPair = stat;
        }
      }
    }
  });

  // ======================================================
  // (3) Quality calculation
  // ======================================================
  let quality = 'unknown';
  if (bitrateKbps >= 64 && packetsPerSecond >= 40) quality = 'excellent';
  else if (bitrateKbps >= 32 && packetsPerSecond >= 20) quality = 'good';
  else if (bitrateKbps > 0) quality = 'poor';

  // ======================================================
  // (4) Build return summary
  // ======================================================
  const summary = {
    bitrateKbps,
    packetsPerSecond,
    bytesRecvAudio,
    packetsRecvAudio,
    quality,
    updatedAt: now,

    // Extended debug:
    bytesRecvVideo,
    packetsRecvVideo,
  };

  // ======================================================
  // (5) Attach best nominated/selected candidate pair
  // ======================================================
  if (nominatedPair) {
    const local = candidatesById[nominatedPair.localCandidateId];
    const remote = candidatesById[nominatedPair.remoteCandidateId];

    summary.selectedPair = {
      id: nominatedPair.id,
      state: nominatedPair.state,
      nominated: !!nominatedPair.nominated,
      selected: !!nominatedPair.selected,
      bytesSent: nominatedPair.bytesSent,
      bytesReceived: nominatedPair.bytesReceived,
      localCandidateId: nominatedPair.localCandidateId,
      remoteCandidateId: nominatedPair.remoteCandidateId,

      local: local
        ? {
            id: local.id,
            type: local.candidateType || local.type,
            address: local.address || local.ip,
            port: local.port || local.networkPort,
            protocol: local.protocol,
          }
        : null,

      remote: remote
        ? {
            id: remote.id,
            type: remote.candidateType || remote.type,
            address: remote.address || remote.ip,
            port: remote.port || remote.networkPort,
            protocol: remote.protocol,
          }
        : null,
    };
  }

  return { summary };
}

// --- CallProvider ---
export const CallProvider = ({ children }) => {
  // Socket may be either the raw socket or { socket, ... } depending on provider
  const socketCtx = useSocket();
  const socket =
    socketCtx && socketCtx.emit
      ? socketCtx
      : socketCtx && socketCtx.socket
      ? socketCtx.socket
      : null;

  const { player } = useContext(AuthContext);

  const [active, setActive] = useState(null); // { callId, convoId, peerUserId, isVideo, startedAt }
  const [incoming, setIncoming] = useState(null); // { callId, convoId, fromUserId, fromPlayerId, fromName, isVideo, offer, createdAt }
  const [minimized, setMinimized] = useState(false);
  const [connectionState, setConnectionState] = useState('idle'); // idle | dialing | ringing | connected | ended
  const [peerStats, setPeerStats] = useState(null); // for signal strength / RTC stats UI
  const [isIncomingRinging, setIsIncomingRinging] = useState(false);
  const [isOutgoingRinging, setIsOutgoingRinging] = useState(false);

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const localStreamRef = useRef(null);
  const sessionRef = useRef(null); // createPeerSession result

  const audioContextRef = useRef(null);
  const pendingIceRef = useRef([]); // ICE received before accept() on callee
  const guardRef = useRef({ tearingDown: false });

  const peerUserIdRef = useRef(null);
  const messageDispatchRef = useRef(null);

  const callMessageConvoRef = useRef(null);
  const callMessagePeerRef = useRef(null);
  const jgRingtoneRef = useRef(null);
  const lastStatsTsRef = useRef(0);
  const statsTimerRef = useRef(null);
  const callStartedAtRef = useRef(null);
  const callIdRef = useRef(null);

  // New: track role for high-res debug (caller / callee / idle)
  const roleRef = useRef('idle');

  // New: previous RTC stats for bitrate / PPS deltas
  const statsPrevRef = useRef({});

  // New: caller / callee timeout refs for missed-call behavior
  const callerTimeoutRef = useRef(null);
  const calleeTimeoutRef = useRef(null);

  // New: refs to keep latest state for timeout callbacks
  const activeRef = useRef(null);
  const incomingRef = useRef(null);
  const connectionStateRef = useRef('idle');
  const playerRef = useRef(player);
  const endCallRef = useRef(null);

  // Debug epoch for relative T+XXXms traces
  const debugEpochRef = useRef(null);
  const ensureDebugEpoch = () => {
    if (!debugEpochRef.current) {
      debugEpochRef.current = Date.now();
    }
    return debugEpochRef.current;
  };
  const t = () => {
    const base = ensureDebugEpoch();
    return Date.now() - base;
  };

  const userId = player?.data?.player?._id || player?.data?.player?.id;

  // Keep refs in sync with state for timeout-safe checks
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    incomingRef.current = incoming;
  }, [incoming]);

  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  useEffect(() => {
    playerRef.current = player;
  }, [player]);

  // --- AudioContext init ---
  useEffect(() => {
    if (!AudioContextClass) return;
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }
  }, []);

  // --- DEBUG: log when local / remote streams change (who sees what) ---
  useEffect(() => {
    if (!__JG_DEBUG) return;
    const role = roleRef.current;
    if (!localStream) {
      console.log(
        `[T+${t()}ms][CallContext][Media] ${role} localStream cleared`,
      );
      return;
    }
    console.log(
      `[T+${t()}ms][CallContext][Media] ${role} localStream updated`,
      {
        audioTracks: localStream.getAudioTracks().map((tr) => ({
          id: tr.id,
          readyState: tr.readyState,
          enabled: tr.enabled,
          kind: tr.kind,
        })),
        videoTracks: localStream.getVideoTracks().map((tr) => ({
          id: tr.id,
          readyState: tr.readyState,
          enabled: tr.enabled,
          kind: tr.kind,
        })),
      },
    );
  }, [localStream]);

  useEffect(() => {
    if (!__JG_DEBUG) return;
    const role = roleRef.current;
    if (!remoteStream) {
      console.log(
        `[T+${t()}ms][CallContext][Media] ${role} remoteStream cleared`,
      );
      return;
    }
    console.log(
      `[T+${t()}ms][CallContext][Media] ${role} remoteStream updated`,
      {
        audioTracks: remoteStream.getAudioTracks().map((tr) => ({
          id: tr.id,
          readyState: tr.readyState,
          enabled: tr.enabled,
          kind: tr.kind,
        })),
        videoTracks: remoteStream.getVideoTracks().map((tr) => ({
          id: tr.id,
          readyState: tr.readyState,
          enabled: tr.enabled,
          kind: tr.kind,
        })),
      },
    );
  }, [remoteStream]);

  // --- RTC stats polling: keep / reuse your signal strength indicator ---
  useEffect(() => {
    const session = sessionRef.current;
    const pc = session?.pc;

    if (__JG_DEBUG) {
      console.log(`[T+${t()}ms][CallContext][Stats] effect init`, {
        hasPc: !!pc,
        activeCallId: active?.callId,
        callIdRef: callIdRef.current,
        role: roleRef.current,
        pcState: pc?.connectionState,
        iceConnectionState: pc?.iceConnectionState,
        signalingState: pc?.signalingState,
      });
    }

    if (!pc || !active?.callId) {
      if (statsTimerRef.current) {
        clearInterval(statsTimerRef.current);
        statsTimerRef.current = null;
      }
      setPeerStats(null);
      return;
    }

    if (statsTimerRef.current) {
      clearInterval(statsTimerRef.current);
    }

    const intervalMs = 1500;
    statsTimerRef.current = setInterval(async () => {
      try {
        const statsReport = await pc.getStats();
        const summary = computeRtcStats(statsReport, statsPrevRef);
        lastStatsTsRef.current = Date.now();
        setPeerStats(summary);
        if (__JG_DEBUG) {
          console.log(`[T+${t()}ms][CallContext][Stats] update`, {
            summary,
            callIdRef: callIdRef.current,
            role: roleRef.current,
          });
        }
      } catch (e) {
        if (__JG_DEBUG) {
          console.log(`[T+${t()}ms][CallContext][Stats] error`, e);
        }
        // swallow
      }
    }, intervalMs);

    return () => {
      if (statsTimerRef.current) {
        clearInterval(statsTimerRef.current);
        statsTimerRef.current = null;
      }
    };
  }, [active?.callId]);

  // --- helpers for timeout cleanup ---
  const clearCallTimeouts = () => {
    if (callerTimeoutRef.current) {
      clearTimeout(callerTimeoutRef.current);
      callerTimeoutRef.current = null;
    }
    if (calleeTimeoutRef.current) {
      clearTimeout(calleeTimeoutRef.current);
      calleeTimeoutRef.current = null;
    }
  };

  // --- helper: emit a system call message into the conversation ---
  const emitSystemCallMessage = async (text) => {
    try {
      const auth = playerRef.current;
      if (!auth?.data?.player?._id || !socket) return;

      const sender = auth.data.player._id;
      const senderPlayerId = auth.data.player.playerId;
      const convoId =
        activeRef.current?.convoId ||
        incomingRef.current?.convoId ||
        callMessageConvoRef.current ||
        null;
      const receiverId = peerUserIdRef.current || callMessagePeerRef.current;

      if (!convoId || !receiverId) {
        if (__JG_DEBUG) {
          console.log(
            `[T+${t()}ms][CallContext][emitSystemCallMessage] missing convo/receiver`,
            {
              convoId,
              receiverId,
            },
          );
        }
        return;
      }

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][emitSystemCallMessage]', {
          text,
          convoId,
          receiverId,
        });
      }

      const res = await axiosConn.post(
        '/messages/false',
        {
          sender,
          text,
          img: '',
          conversationId: convoId,
          isRead: false,
        },
        {
          headers: { Authorization: `Bearer ${auth.token}` },
        },
      );

      const saved = res?.data?.data?.data;
      messageDispatchRef.current?.({
        type: 'APPEND',
        payload: saved,
      });

      // Let Messenger.jsx append it in real-time via existing socket handlers
      socket.emit('sendMessage', {
        senderId: sender,
        senderPlayerId,
        receiverId,
        text,
        img: '',
        messageId: saved._id,
        isRead: false,

        // 🔥 New flag — tells Messenger not to re-append this message
        localOnly: true,
      });
    } catch (e) {
      if (__JG_DEBUG) {
        console.log(
          '[T+' + t() + 'ms][CallContext][emitSystemCallMessage] error',
          e,
        );
      }
    }
  };

  // --- helper: stop all media / tear down ---
  const hardTeardown = useCallback(
    async ({ reason } = {}) => {
      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Teardown] start', {
          reason,
          hasSession: !!sessionRef.current,
          hasLocalStream: !!localStreamRef.current,
          callIdRef: callIdRef.current,
          active,
          incoming,
          role: roleRef.current,
        });
      }

      guardRef.current.tearingDown = true;

      // Clear any pending caller/callee timeouts
      clearCallTimeouts();

      try {
        stopRingtone(jgRingtoneRef.current);
      } catch (e) {}

      setIsIncomingRinging(false);
      setIsOutgoingRinging(false);

      if (statsTimerRef.current) {
        clearInterval(statsTimerRef.current);
        statsTimerRef.current = null;
      }
      setPeerStats(null);

      // Close session + PC
      const session = sessionRef.current;
      try {
        session?.close(localStreamRef.current);
      } catch (e) {
        // ignore
      }
      sessionRef.current = null;

      // Stop local media tracks
      if (localStreamRef.current) {
        try {
          localStreamRef.current.getTracks().forEach((tTrack) => tTrack.stop());
        } catch (e) {}
        localStreamRef.current = null;
      }
      setLocalStream(null);

      // Clear remote
      setRemoteStream(null);
      peerUserIdRef.current = null;
      pendingIceRef.current = [];

      setIncoming(null);
      setActive(null);
      setConnectionState('idle');
      callStartedAtRef.current = null;
      callIdRef.current = null;
      roleRef.current = 'idle';

      guardRef.current.tearingDown = false;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Teardown] complete', {
          reason,
        });
      }
    },
    [active, incoming, t],
  );

  // --- startCall (audio/video) using session API --- (caller)
  const startCall = useCallback(
    async (opts = {}) => {
      const {
        mode = 'audio',
        convoId,
        peerId: explicitPeerId,
        isVideo: isVideoFlag,
        video,
        withVideo,
      } = opts;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][startCall] invoked', {
          opts,
          userId,
          activeCallId: active?.callId,
          incomingCallId: incoming?.callId,
        });
      }

      if (!socket || !userId || !convoId) return;
      if (guardRef.current.tearingDown) return;

      roleRef.current = 'caller';

      // Robust video intent detection
      const isVideoIntent =
        mode === 'video' ||
        isVideoFlag === true ||
        video === true ||
        withVideo === true;

      // Already in a call or ringing? Do nothing.
      if (active?.callId || incoming?.callId) return;

      // Pre-allocate callId so early ICE always carries a stable ID
      const callId = callIdRef.current || convoId || `call-${Date.now()}`;
      callIdRef.current = callId;
      peerUserIdRef.current = explicitPeerId || null;

      try {
        if (__JG_DEBUG) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] ensureLocalMedia',
            {
              isVideoIntent,
            },
          );
        }

        const stream = await ensureLocalMedia({
          audio: true,
          video: isVideoIntent,
        });

        if (__JG_DEBUG && stream) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] got localStream',
            {
              role: roleRef.current,
              audioTracks: stream.getAudioTracks().map((tr) => tr.id),
              videoTracks: stream.getVideoTracks().map((tr) => tr.id),
            },
          );
        }

        localStreamRef.current = stream;
        setLocalStream(stream);

        // Create session (track-first)
        const session = createPeerSession({
          wantVideo: isVideoIntent,
          onRemoteStream: (s) => {
            if (__JG_DEBUG) {
              console.log(
                '[T+' +
                  t() +
                  'ms][CallContext][startCall][onRemoteStream] (caller)',
                {
                  hasStream: !!s,
                  audioTracks: s ? s.getAudioTracks().map((tr) => tr.id) : [],
                  videoTracks: s ? s.getVideoTracks().map((tr) => tr.id) : [],
                },
              );
            }
            if (s) setRemoteStream(s);
          },
          onConnectionState: (state) => {
            if (process.env.NODE_ENV !== 'production') {
              console.log(
                '[T+' + t() + 'ms][CallContext] PC connectionState (caller):',
                state,
              );
            }
          },
        });

        sessionRef.current = session;
        const pc = session.pc;
        if (__JG_DEBUG && pc) {
          const senders = pc.getSenders ? pc.getSenders() : [];
          const trs = pc.getTransceivers ? pc.getTransceivers() : [];
          console.log(
            '[T+' +
              t() +
              'ms][CallContext][startCall] created session+pc (caller)',
            {
              pcState: pc.connectionState,
              iceConnectionState: pc.iceConnectionState,
              signalingState: pc.signalingState,
              senders: senders.map((s) => ({
                kind: s.track?.kind,
                id: s.track?.id,
                readyState: s.track?.readyState,
              })),
              transceivers: trs.map((tr) => ({
                mid: tr.mid,
                direction: tr.direction,
                currentDirection: tr.currentDirection,
                kind: tr.receiver?.track?.kind || tr.kind,
              })),
            },
          );
        }

        // Local ICE → send to server (legacy + new)
        session.onLocalIceCandidate?.((candidate) => {
          if (!candidate || !socket) return;
          const icePayload = {
            candidate,
            convoId,
            callId: callIdRef.current,
            to: explicitPeerId,
          };
          if (__JG_DEBUG) {
            console.log(
              '[T+' +
                t() +
                'ms][CallContext][startCall][onLocalIceCandidate] (caller)',
              {
                candidateType: candidate.candidate,
                convoId,
                callId: callIdRef.current,
              },
            );
          }
          socket.emit('call:candidate', icePayload);
          socket.emit('webrtc:ice', icePayload);
        });

        // Attach local stream tracks
        if (__JG_DEBUG) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] addLocalStream (caller)',
          );
        }
        await session.addLocalStream(stream);

        if (__JG_DEBUG && pc) {
          const sendersAfter = pc.getSenders ? pc.getSenders() : [];
          const trsAfter = pc.getTransceivers ? pc.getTransceivers() : [];
          console.log(
            '[T+' +
              t() +
              'ms][CallContext][startCall] after addLocalStream (caller)',
            {
              senders: sendersAfter.map((s) => ({
                kind: s.track?.kind,
                id: s.track?.id,
                readyState: s.track?.readyState,
              })),
              transceivers: trsAfter.map((tr) => ({
                mid: tr.mid,
                direction: tr.direction,
                currentDirection: tr.currentDirection,
                kind: tr.receiver?.track?.kind || tr.kind,
              })),
            },
          );
        }

        // Create offer from session
        if (__JG_DEBUG) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] createOffer (caller)',
          );
        }
        const offer = await session.createOffer();

        if (__JG_DEBUG) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] offer created (caller)',
            {
              hasSdp: !!offer?.sdp,
            },
          );
        }

        const offerPayload = {
          offer,
          convoId,
          callId,
          to: explicitPeerId,
          withVideo: isVideoIntent,
          isVideo: isVideoIntent,
        };

        // Send offer over signaling (legacy + new)
        if (__JG_DEBUG) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] emit offer (caller)',
            {
              convoId,
              callId,
              isVideoIntent,
            },
          );
        }
        socket.emit('call:offer', offerPayload);
        socket.emit('webrtc:offer', offerPayload);

        callStartedAtRef.current = Date.now();
        setActive({
          callId,
          convoId,
          peerUserId: explicitPeerId,
          isVideo: isVideoIntent,
          startedAt: callStartedAtRef.current,
        });
        // store convo/peer for missed-call message
        callMessageConvoRef.current = convoId;
        callMessagePeerRef.current = explicitPeerId;

        setConnectionState('dialing');

        if (__JG_DEBUG) {
          console.log(
            '[T+' + t() + 'ms][CallContext][startCall] play outgoing ringtone',
          );
        }
        playRingtone(jgRingtoneRef.current, {
          kind: 'outgoing',
          url: OUTGOING_RING_URL,
          segmentSeconds: RING_SEGMENT_SECONDS,
        });
        setIsOutgoingRinging(true);

        // --- Caller timeout: missed call (20s) ---
        if (callerTimeoutRef.current) {
          clearTimeout(callerTimeoutRef.current);
        }
        callerTimeoutRef.current = setTimeout(async () => {
          if (__JG_DEBUG) {
            console.log(
              '[T+' +
                t() +
                'ms][CallContext][Timeout][caller] fired (20s no answer)',
              {
                activeRef: activeRef.current,
                connectionState: connectionStateRef.current,
                callIdRef: callIdRef.current,
              },
            );
          }

          const currentActive = activeRef.current;
          if (!currentActive || currentActive.callId !== callIdRef.current) {
            return;
          }
          if (connectionStateRef.current === 'connected') {
            return;
          }

          try {
            if (endCallRef.current) {
              await endCallRef.current({ reason: 'timeout' });
            }
            await emitSystemCallMessage(MISSED_CALL_MESSAGE);
          } catch (e) {
            if (__JG_DEBUG) {
              console.log(
                '[T+' +
                  t() +
                  'ms][CallContext][Timeout][caller] error sending missed msg',
                e,
              );
            }
          }
        }, 20000);
      } catch (err) {
        console.error('[CallContext] startCall error', err);
        await hardTeardown({ reason: 'start-failed' });
      }
    },
    [socket, userId, active, incoming, hardTeardown, t],
  );

  // --- accept incoming call (callee) ---
  const acceptCall = useCallback(async () => {
    if (!incoming?.callId || !socket || guardRef.current.tearingDown) return;
    const incomingCall = incoming;

    roleRef.current = 'callee';

    if (__JG_DEBUG) {
      console.log('[T+' + t() + 'ms][CallContext][acceptCall] invoked', {
        incomingCall,
      });
    }

    // Accepting cancels any pending callee timeout
    clearCallTimeouts();

    try {
      const isVideoIntent = !!(incomingCall.isVideo || incomingCall.withVideo);

      // Acquire local media based on caller intent
      if (__JG_DEBUG) {
        console.log(
          '[T+' + t() + 'ms][CallContext][acceptCall] ensureLocalMedia',
          { isVideoIntent },
        );
      }
      const stream = await ensureLocalMedia({
        audio: true,
        video: isVideoIntent,
      });

      if (__JG_DEBUG && stream) {
        console.log(
          '[T+' + t() + 'ms][CallContext][acceptCall] got localStream',
          {
            role: roleRef.current,
            audioTracks: stream.getAudioTracks().map((tr) => tr.id),
            videoTracks: stream.getVideoTracks().map((tr) => tr.id),
          },
        );
      }

      localStreamRef.current = stream;
      setLocalStream(stream);

      // Create session
      const session = createPeerSession({
        wantVideo: isVideoIntent,
        onRemoteStream: (s) => {
          if (__JG_DEBUG) {
            console.log(
              '[T+' +
                t() +
                'ms][CallContext][acceptCall][onRemoteStream] (callee)',
              {
                hasStream: !!s,
                audioTracks: s ? s.getAudioTracks().map((tr) => tr.id) : [],
                videoTracks: s ? s.getVideoTracks().map((tr) => tr.id) : [],
              },
            );
          }
          if (s) setRemoteStream(s);
        },
        onConnectionState: (state) => {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '[T+' + t() + 'ms][CallContext] acceptCall PC state (callee):',
              state,
            );
          }
        },
      });

      sessionRef.current = session;
      peerUserIdRef.current = incomingCall.fromUserId || incomingCall.from;
      callIdRef.current = incomingCall.callId || incomingCall.convoId;

      const pc = session.pc;
      if (__JG_DEBUG && pc) {
        const senders = pc.getSenders ? pc.getSenders() : [];
        const trs = pc.getTransceivers ? pc.getTransceivers() : [];
        console.log(
          '[T+' + t() + 'ms][CallContext][acceptCall] session created (callee)',
          {
            callId: callIdRef.current,
            convoId: incomingCall.convoId,
            pcState: pc.connectionState,
            iceConnectionState: pc.iceConnectionState,
            signalingState: pc.signalingState,
            senders: senders.map((s) => ({
              kind: s.track?.kind,
              id: s.track?.id,
              readyState: s.track?.readyState,
            })),
            transceivers: trs.map((tr) => ({
              mid: tr.mid,
              direction: tr.direction,
              currentDirection: tr.currentDirection,
              kind: tr.receiver?.track?.kind || tr.kind,
            })),
          },
        );
      }

      // Local ICE → send to caller (legacy + new)
      session.onLocalIceCandidate?.((candidate) => {
        if (!candidate || !socket) return;
        const icePayload = {
          candidate,
          convoId: incomingCall.convoId,
          callId: incomingCall.callId || callIdRef.current,
          to: incomingCall.fromUserId || incomingCall.from,
        };
        if (__JG_DEBUG) {
          console.log(
            '[T+' +
              t() +
              'ms][CallContext][acceptCall][onLocalIceCandidate] (callee)',
            {
              candidateType: candidate.candidate,
              convoId: incomingCall.convoId,
              callId: incomingCall.callId || callIdRef.current,
            },
          );
        }
        socket.emit('call:candidate', icePayload);
        socket.emit('webrtc:ice', icePayload);
      });

      // Attach local tracks
      if (__JG_DEBUG) {
        console.log(
          '[T+' + t() + 'ms][CallContext][acceptCall] addLocalStream (callee)',
        );
      }
      await session.addLocalStream(stream);

      if (__JG_DEBUG && pc) {
        const sendersAfter = pc.getSenders ? pc.getSenders() : [];
        const trsAfter = pc.getTransceivers ? pc.getTransceivers() : [];
        console.log(
          '[T+' +
            t() +
            'ms][CallContext][acceptCall] after addLocalStream (callee)',
          {
            senders: sendersAfter.map((s) => ({
              kind: s.track?.kind,
              id: s.track?.id,
              readyState: s.track?.readyState,
            })),
            transceivers: trsAfter.map((tr) => ({
              mid: tr.mid,
              direction: tr.direction,
              currentDirection: tr.currentDirection,
              kind: tr.receiver?.track?.kind || tr.kind,
            })),
          },
        );
      }

      // Apply remote offer + create answer
      if (__JG_DEBUG) {
        console.log(
          '[T+' +
            t() +
            'ms][CallContext][acceptCall] setRemoteOfferAndCreateAnswer',
          { hasOffer: !!(incomingCall.offer || incomingCall.sdp) },
        );
      }
      const answer = await session.setRemoteOfferAndCreateAnswer({
        offer: incomingCall.offer || incomingCall.sdp,
      });

      if (__JG_DEBUG) {
        console.log(
          '[T+' + t() + 'ms][CallContext][acceptCall] answer created (callee)',
          {
            hasSdp: !!answer?.sdp,
            bufferedIceCount: pendingIceRef.current.length,
          },
        );
      }

      // Flush any ICE that arrived before accept
      if (pendingIceRef.current.length) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '[T+' + t() + 'ms][CallContext] flushing buffered ICE on accept:',
            pendingIceRef.current.length,
          );
        }
        for (const { candidate } of pendingIceRef.current) {
          try {
            await session.addRemoteIceCandidate(candidate);
          } catch (e) {
            console.error('[CallContext] flush ICE error', e);
          }
        }
        pendingIceRef.current = [];
      }

      const answerPayload = {
        answer,
        convoId: incomingCall.convoId,
        callId: incomingCall.callId || callIdRef.current,
        to: incomingCall.fromUserId || incomingCall.from,
        isVideo: isVideoIntent,
        withVideo: isVideoIntent,
      };

      // Send answer to caller (legacy + new)
      if (__JG_DEBUG) {
        console.log(
          '[T+' + t() + 'ms][CallContext][acceptCall] emit answer (callee)',
          {
            convoId: incomingCall.convoId,
            callId: incomingCall.callId || callIdRef.current,
            isVideoIntent,
          },
        );
      }
      socket.emit('call:answer', answerPayload);
      socket.emit('webrtc:answer', answerPayload);

      setActive({
        callId: incomingCall.callId || callIdRef.current,
        convoId: incomingCall.convoId,
        peerUserId: incomingCall.fromUserId || incomingCall.from,
        isVideo: isVideoIntent,
        startedAt: Date.now(),
      });
      callStartedAtRef.current = Date.now();
      setIncoming(null);
      setConnectionState('connected');

      stopRingtone(jgRingtoneRef.current);
      setIsIncomingRinging(false);
    } catch (err) {
      console.error('[CallContext] acceptCall error', err);
      await hardTeardown({ reason: 'accept-failed' });
    }
  }, [incoming, socket, hardTeardown, t]);

  // --- decline incoming call (callee explicit decline) ---
  const declineCall = useCallback(
    async ({ silent } = {}) => {
      if (!incoming?.callId || !socket) return;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][declineCall]', {
          incoming,
          silent,
        });
      }

      const targetUserId = incoming.fromUserId || incoming.from;
      const callId = incoming.callId;

      // Any explicit decline cancels callee timeout
      clearCallTimeouts();

      socket.emit('call:decline', {
        to: targetUserId,
        convoId: incoming.convoId,
        callId,
      });

      if (!silent) {
        try {
          await axiosConn.post(`call-log/decline`, {
            callId,
            from: targetUserId,
            to: userId,
          });
        } catch (e) {
          // ignore analytics failures
        }

        // Emit "Declined call." as a chat message from the callee
        try {
          await emitSystemCallMessage(DECLINED_CALL_MESSAGE);
        } catch (e) {
          if (__JG_DEBUG) {
            console.log(
              '[T+' +
                t() +
                'ms][CallContext][declineCall] emit declined msg error',
              e,
            );
          }
        }
      }

      setIncoming(null);
      stopRingtone(jgRingtoneRef.current);
      setIsIncomingRinging(false);
      if (!active?.callId) {
        setConnectionState('idle');
      }
    },
    [incoming, socket, userId, active, t],
  );

  // --- end active call ---
  const endCall = useCallback(
    async ({ reason = 'hangup' } = {}) => {
      if (!socket) return;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][endCall] invoked', {
          reason,
          active,
          incoming,
        });
      }

      // Any endCall kills caller/callee timeouts
      clearCallTimeouts();

      if (!active?.callId && !incoming?.callId) {
        await hardTeardown({ reason: 'no-active' });
        return;
      }

      const callId = active?.callId || incoming?.callId;
      const targetUserId =
        active?.peerUserId || incoming?.fromUserId || incoming?.from;

      socket.emit('call:end', {
        to: targetUserId,
        convoId: active?.convoId || incoming?.convoId,
        callId,
        reason,
      });

      playHangupSound(HANGUP_SOUND_URL);

      try {
        await axiosConn.post(`call-log/end`, {
          callId,
          from: userId,
          to: targetUserId,
          reason,
          durationSeconds: callStartedAtRef.current
            ? Math.round((Date.now() - callStartedAtRef.current) / 1000)
            : 0,
        });
      } catch (e) {
        // ignore analytics failures
      }

      await hardTeardown({ reason });
    },
    [socket, active, incoming, userId, hardTeardown, t],
  );

  // Keep ref to latest endCall for timeout usage
  useEffect(() => {
    endCallRef.current = endCall;
  }, [endCall]);

  // --- incoming signaling events via callEvents (mitt) ---
  useEffect(() => {
    if (!socket) return;

    // Incoming ringing / offer (normalized by socket.js)
    const handleIncoming = (payload) => {
      if (!payload) return;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Signal][incoming]', {
          payload,
          activeCallId: active?.callId,
          incomingCallId: incoming?.callId,
        });
      }

      const callId =
        payload.callId ||
        payload.callID ||
        payload.convoId ||
        `call-${Date.now()}`;
      const fromUserId = payload.fromUserId || payload.from;

      // Already in a call → busy
      if (active?.callId || incoming?.callId) {
        if (__JG_DEBUG) {
          console.log('[T+' + t() + 'ms][CallContext][Signal][incoming] busy', {
            fromUserId,
          });
        }
        if (socket) {
          socket.emit('call:busy', {
            to: fromUserId,
            convoId: payload.convoId,
            callId,
          });
        }
        return;
      }

      callIdRef.current = callId;
      peerUserIdRef.current = fromUserId;
      pendingIceRef.current = [];
      roleRef.current = 'callee';

      const offer =
        payload.offer ||
        (payload.sdp ? { type: 'offer', sdp: payload.sdp } : null);

      setIncoming({
        callId,
        convoId: payload.convoId,
        fromUserId,
        fromPlayerId: payload.fromPlayerId || payload.playerId,
        fromName:
          payload.fromName ||
          payload.fromPlayerId ||
          payload.playerId ||
          'Incoming call',
        fromProfilePhoto:
          payload.fromProfilePhoto ||
          payload.profilePhoto ||
          payload.avatarUrl ||
          payload.avatar ||
          null,
        isVideo: !!payload.isVideo,
        withVideo: !!payload.withVideo,
        offer,
        createdAt: Date.now(),
      });

      if (__JG_DEBUG) {
        console.log(
          '[T+' + t() + 'ms][CallContext][Signal][incoming] setIncoming',
          {
            callId,
            fromUserId,
          },
        );
      }

      playRingtone(jgRingtoneRef.current, {
        kind: 'incoming',
        url: INCOMING_RING_URL,
        segmentSeconds: RING_SEGMENT_SECONDS,
      });
      setIsIncomingRinging(true);
      setConnectionState('ringing');
      setMinimized(false);

      // --- Callee timeout: auto-clear after 20s with no answer/decline ---
      if (calleeTimeoutRef.current) {
        clearTimeout(calleeTimeoutRef.current);
      }
      calleeTimeoutRef.current = setTimeout(() => {
        if (__JG_DEBUG) {
          console.log(
            '[T+' +
              t() +
              'ms][CallContext][Timeout][callee] fired (20s no action)',
            {
              incomingRef: incomingRef.current,
              activeRef: activeRef.current,
              connectionState: connectionStateRef.current,
            },
          );
        }

        const inc = incomingRef.current;
        const act = activeRef.current;
        // If call already accepted or cleared, do nothing
        if (!inc || act?.callId) return;

        // Auto-clear incoming UI – caller side will already send missed-call msg
        stopRingtone(jgRingtoneRef.current);
        setIsIncomingRinging(false);
        setIncoming(null);
        setConnectionState('idle');
        roleRef.current = 'idle';
      }, 20000);
    };

    // Caller receives answer
    const handleAnswer = async (payload) => {
      if (!sessionRef.current || !active?.callId) return;
      const raw = payload.raw || {};
      const resolvedCallId =
        raw.callId || raw.convoId || callIdRef.current || active.callId;

      const pc = sessionRef.current?.pc;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Signal][answer]', {
          payload,
          resolvedCallId,
          callIdRef: callIdRef.current,
          activeCallId: active?.callId,
          role: roleRef.current,
          pcBefore: pc
            ? {
                signalingState: pc.signalingState,
                connectionState: pc.connectionState,
                iceConnectionState: pc.iceConnectionState,
              }
            : null,
        });
      }

      if (
        callIdRef.current &&
        resolvedCallId &&
        callIdRef.current !== resolvedCallId
      ) {
        return;
      }

      try {
        await sessionRef.current.setRemoteAnswer(
          payload.sdp || payload.answer || payload,
        );
        if (__JG_DEBUG && pc) {
          console.log(
            '[T+' +
              t() +
              'ms][CallContext][Signal][answer] after setRemoteAnswer',
            {
              pcAfter: {
                signalingState: pc.signalingState,
                connectionState: pc.connectionState,
                iceConnectionState: pc.iceConnectionState,
              },
            },
          );
        }

        // Once answered, caller timeout no longer applies
        clearCallTimeouts();

        setConnectionState('connected');
        stopRingtone(jgRingtoneRef.current);
        setIsOutgoingRinging(false);
      } catch (e) {
        console.error('[CallContext] handleAnswer error', e);
      }
    };

    // ICE from remote (both caller + callee)
    const handleIce = async (payload) => {
      if (!payload || !payload.candidate) return;
      const raw = payload.raw || {};
      const resolvedCallId =
        raw.callId || raw.convoId || callIdRef.current || null;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Signal][ice]', {
          payload,
          resolvedCallId,
          incomingCallId: incoming?.callId,
          activeCallId: active?.callId,
          bufferedCount: pendingIceRef.current.length,
          role: roleRef.current,
        });
      }

      // If ICE arrives for an incoming call *before* accept, buffer (callee side)
      if (
        incoming?.callId &&
        resolvedCallId &&
        resolvedCallId === incoming.callId &&
        !active?.callId
      ) {
        pendingIceRef.current.push({ candidate: payload.candidate });
        return;
      }

      const session = sessionRef.current;
      if (!session) return;

      const activeCallId = active?.callId || callIdRef.current || null;
      if (activeCallId && resolvedCallId && resolvedCallId !== activeCallId) {
        // Different call; ignore
        return;
      }

      try {
        await session.addRemoteIceCandidate(payload.candidate);
      } catch (e) {
        console.error('[CallContext] handleIce error', e);
      }
    };

    // Remote end (hangup)
    const handleEnded = async (payload) => {
      const raw = payload || {};
      const resolvedCallId = raw.callId || raw.convoId;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Signal][ended]', {
          payload,
          resolvedCallId,
          callIdRef: callIdRef.current,
        });
      }

      if (
        callIdRef.current &&
        resolvedCallId &&
        callIdRef.current !== resolvedCallId
      ) {
        return;
      }

      await hardTeardown({ reason: raw.reason || 'remote-end' });
    };

    // Remote cancelled before answer
    const handleCancelled = async (payload) => {
      const raw = payload || {};
      const resolvedCallId = raw.callId || raw.convoId;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Signal][cancelled]', {
          payload,
          resolvedCallId,
          incomingCallId: incoming?.callId,
          activeCallId: active?.callId,
        });
      }

      // Any remote cancel should clear local timeouts
      clearCallTimeouts();

      if (
        incoming?.callId &&
        resolvedCallId === incoming.callId &&
        !active?.callId
      ) {
        stopRingtone(jgRingtoneRef.current);
        setIsIncomingRinging(false);
        setIncoming(null);
        setConnectionState('idle');
        roleRef.current = 'idle';
        return;
      }

      if (active?.callId && resolvedCallId === active.callId) {
        await hardTeardown({ reason: 'remote-cancelled' });
      }
    };

    const handleTimeout = async (payload) => {
      const raw = payload || {};
      const resolvedCallId = raw.callId || raw.convoId;

      if (__JG_DEBUG) {
        console.log('[T+' + t() + 'ms][CallContext][Signal][timeout]', {
          payload,
          resolvedCallId,
          incomingCallId: incoming?.callId,
          activeCallId: active?.callId,
        });
      }

      // Server-side timeout: clear local timeouts too
      clearCallTimeouts();

      if (
        incoming?.callId &&
        resolvedCallId === incoming.callId &&
        !active?.callId
      ) {
        stopRingtone(jgRingtoneRef.current);
        setIsIncomingRinging(false);
        setIncoming(null);
        setConnectionState('idle');
        roleRef.current = 'idle';
      }

      if (active?.callId && resolvedCallId === active.callId) {
        await hardTeardown({ reason: 'timeout' });
      }
    };

    callEvents.on('incoming', handleIncoming);
    callEvents.on('answer', handleAnswer);
    callEvents.on('ice', handleIce);
    callEvents.on('ended', handleEnded);
    callEvents.on('cancelled', handleCancelled);
    callEvents.on('timeout', handleTimeout);

    return () => {
      callEvents.off('incoming', handleIncoming);
      callEvents.off('answer', handleAnswer);
      callEvents.off('ice', handleIce);
      callEvents.off('ended', handleEnded);
      callEvents.off('cancelled', handleCancelled);
      callEvents.off('timeout', handleTimeout);
    };
  }, [socket, active, incoming, hardTeardown, t]);

  // --- minimize / restore overlay ---
  const minimize = useCallback(() => setMinimized(true), []);
  const restore = useCallback(() => setMinimized(false), []);

  const value = {
    active,
    incoming,
    minimized,
    connectionState,
    localStream,
    remoteStream,
    isIncomingRinging,
    isOutgoingRinging,
    peerStats, // <- drives your JGSignalStrength / RTC stats UI

    startCall,
    acceptCall,
    declineCall,
    endCall,
    minimize,
    messageDispatchRef,
    restore,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};

export const useCall = () => useContext(CallContext);
